import React, { useState } from "react";
import { Icon } from "../../../components/Icon/Icon";
import { Button } from "../../../components/Button/Button";
import Box from "../../../primitives/Box/Box";
import Tags from "../../../components/Tags/Tags";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import {
  Body5RegularTypography,
  Body6RegularTypography,
  Caps2SemiboldTypography,
} from "../../../primitives/Typography/typographyVariants";
import { colorPalette } from "../../../theme/colors";
import { AudioPlayer } from "../AudioPlayer/AudioPlayer";
import { StyledLink } from "../MessageCard/MessageCard.styles";
import { ScriptMessageCardProps } from "./ScriptMessageCard.types";
import {
  ScriptContentArea,
  StyledScriptText,
  ExpandButton,
} from "./ScriptMessageCard.styles";

const splitUrlSegments = (text: string): string[] =>
  text.split(/(https?:\/\/[^\s]+)/gi);
const isUrlSegment = (s: string) => /^https?:\/\//i.test(s);

export const ScriptMessageCard: React.FC<ScriptMessageCardProps> = ({
  title = "SCRIPT SUBMISSION",
  versionStatus,
  content,
  onExternalLinkClick,
  status,
  timestamp,
  isEditable = false,
  isActionCompleted = false,
  onEdit,
  dataCy = "script-message-card",
  showTimestamp = true,
  variant = "default",
  onClick,
  isFeedback = false,
  maxHeight,
  subtitle,
  onReferenceClick,
  isReferenceLoading = false,
  width,
  maxWidth,
  onExpand,
  voiceNoteMedia,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const renderSubtitleWithLinks = (raw: string): React.ReactNode[] =>
    splitUrlSegments(raw).map((part, index) => {
      if (!part) return null;
      if (isUrlSegment(part)) {
        return (
          <StyledLink
            key={index}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onExternalLinkClick?.({ redirectUrl: part.trim() });
            }}
          >
            {part}
          </StyledLink>
        );
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });

  const handleEdit = () => {
    if (!isEditable) return;
    onEdit?.();
  };

  const FEEDBACK_LIMIT = 100;
  const NON_FEEDBACK_LIMIT = 500;

  const characterLimit = isFeedback ? FEEDBACK_LIMIT : NON_FEEDBACK_LIMIT;
  const shouldShowExpandButton = !isFeedback && content.length > characterLimit;

  const displayContent =
    isFeedback && content.length > FEEDBACK_LIMIT
      ? `${content.substring(0, FEEDBACK_LIMIT)}...`
      : isExpanded || !shouldShowExpandButton || variant === "scrollable"
      ? content
      : content.substring(0, characterLimit);

  return (
    <FlexColumn
      data-cy={dataCy}
      backgroundColor={colorPalette.popWhite[300]}
      borderRadius="8px"
      pt={12}
      pb={4}
      px={12}
      gap={8}
      onClick={onClick}
      width={width}
      maxWidth={maxWidth}
    >
      {isReferenceLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          borderRadius="8px"
          backgroundColor="rgba(255, 255, 255, 0.9)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={10}
        >
          <FlexColumn alignItems="center" gap={8}>
            <Icon
              name="fa-solid fa-spinner"
              size="24"
              height="24"
              width="24"
              color={colorPalette.popOrange[500]}
              className="fa-spin"
            />
            <Body6RegularTypography color={colorPalette.popBlack[400]}>
              Finding message...
            </Body6RegularTypography>
          </FlexColumn>
        </Box>
      )}
      {title && (
        <Caps2SemiboldTypography
          fontFamily="Lexend"
          color={colorPalette.popBlack[100]}
          className="message-card-title"
        >
          {title}
        </Caps2SemiboldTypography>
      )}

      {versionStatus && (
        <Tags
          variant="primary"
          label={versionStatus.label}
          icon={versionStatus.icon}
          backgroundColor={versionStatus.backgroundColor}
          color={versionStatus.textColor}
          iconColor={versionStatus.iconColor}
        />
      )}

      {/* Content Area */}
      <ScriptContentArea
        variant={variant}
        maxHeight={maxHeight}
        isFeedback={isFeedback}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onReferenceClick?.();
          }
        }}
      >
        <StyledScriptText
          dangerouslySetInnerHTML={{ __html: displayContent }}
          data-cy="script-content"
          isFeedback={isFeedback}
        />

        {/* Inline expand button for feedback content */}
        {shouldShowExpandButton && variant !== "scrollable" && (
          <>
            <ExpandButton
              onClick={(e) => {
                e.stopPropagation(); // Prevent event from bubbling to parent
                setIsExpanded(!isExpanded);
                onExpand?.();
              }}
              data-cy="expand-button"
            >
              {isExpanded ? "show less" : "show more"}
            </ExpandButton>
          </>
        )}
      </ScriptContentArea>
      {voiceNoteMedia && (
        <Box width="100%">
          <AudioPlayer
            src={voiceNoteMedia.src}
            timeMode={"none"}
            showTime={false}
            groupId={voiceNoteMedia.audio?.groupId ?? "default"}
            playerId={voiceNoteMedia.audio?.playerId ?? `voice-script`}
          />
        </Box>
      )}
      {subtitle && (
        <Body5RegularTypography
          as="div"
          width="100%"
          minWidth={0}
          wordBreak="break-word"
          style={{ overflowWrap: "anywhere" }}
          color={colorPalette.popBlack[500]}
          whiteSpace="pre-line"
        >
          {renderSubtitleWithLinks(subtitle)}
        </Body5RegularTypography>
      )}

      {(status || isEditable) && (
        <FlexRow justifyContent={status ? "space-between" : "flex-end"} py={4}>
          {status ? (
            <Tags
              variant="primary"
              label={status.label}
              color={status.textColor}
              backgroundColor={status.backgroundColor}
              icon={status.icon}
              iconColor={status.iconColor}
            />
          ) : (
            <></>
          )}
          {isEditable && (
            <Button
              variant="secondary"
              onClick={handleEdit}
              size="small"
              width="auto"
              disabled={isActionCompleted}
              leftIcon={
                <FlexColumn
                  alignItems="center"
                  justifyContent="center"
                  height="26px"
                  color="inherit"
                >
                  <Icon
                    name="fa-solid fa-pen-to-square"
                    size="12"
                    height="18"
                    width="18"
                    color="inherit"
                  />
                </FlexColumn>
              }
            >
              Edit Script
            </Button>
          )}
        </FlexRow>
      )}

      {/* Timestamp */}
      {showTimestamp && timestamp && (
        <FlexRow justifyContent="flex-end">
          <Body6RegularTypography
            data-cy="timestamp"
            color={colorPalette.popBlack[100]}
          >
            {timestamp}
          </Body6RegularTypography>
        </FlexRow>
      )}
    </FlexColumn>
  );
};
